import type { HierarchyRectangularNode } from '@visx/hierarchy/lib/types.js';
import { forwardRef, useEffect, useState } from 'react';

import { headerText, valueChip } from './TreeMapTooltip.sty.js';
import { useChartActionsMenu } from '../../../core/components/series-action/hooks/useChartActionsMenu.js';
import { mergeTooltipActions } from '../../../core/components/series-action/utils/mergeTooltipActions.js';
import { ChartTooltip } from '../../../core/components/tooltip/ChartTooltip/ChartTooltip.js';
import { TooltipText } from '../../../core/components/tooltip/section-components/atoms/TooltipText.js';
import { TooltipBody } from '../../../core/components/tooltip/section-components/TooltipBody.js';
import { TooltipHeader } from '../../../core/components/tooltip/section-components/TooltipHeader/TooltipHeader.js';
import { TooltipInfoContent } from '../../../core/components/tooltip/section-components/TooltipInfoContent/TooltipInfoContent.js';
import { TooltipItem } from '../../../core/components/tooltip/section-components/TooltipItem/TooltipItem.js';
import { TooltipSymbolContent } from '../../../core/components/tooltip/section-components/TooltipSymbolContent/TooltipSymbolContent.js';
import { TooltipValueContent } from '../../../core/components/tooltip/section-components/TooltipValueContent/TooltipValueContent.js';
import { useTooltipSeriesActions } from '../../../core/components/tooltip/shared/hooks/useTooltipSeriesActions.js';
import { SingleDataPointShape } from '../../../core/components/tooltip-atoms/shapes/SingleDataPointShape.js';
import { TooltipItem as TooltipItemType } from '../../../core/types/tooltip.js';
import { useFormatter } from '../../context/FormatterContext.js';
import { useTreeMapInternalData } from '../../context/TreeMapInternalDataContext.js';
import { useTruncationMode } from '../../hooks/useTruncationMode.js';
import { useOverlayState, useTooltipState } from '../../store/selectors.js';
import type { InternalTreeMapNode } from '../../types/internal.js';
import type { TreeMapSeriesActionsLeafNode } from '../../types/treemap.js';
import { getTooltipItem } from '../../utils/get-tooltip-item.js';

interface TreeMapTooltipProps {
  series: TreeMapSeriesActionsLeafNode[];
}

export const TreeMapTooltip = forwardRef<HTMLDivElement, TreeMapTooltipProps>(
  ({ series }, forwardedRef) => {
    const [tooltipContent, setTooltipContent] = useState<
      HierarchyRectangularNode<InternalTreeMapNode> | undefined
    >();
    const formatter = useFormatter();

    const { visible, pinned, content, position } = useTooltipState();
    const { mouseInBounds } = useOverlayState();

    const truncationMode = useTruncationMode();
    const { unit } = useTreeMapInternalData();

    const {
      selectedItem: selectedItemId,
      actionsMenuRef,
      updateSelectedItem,
      onLeave,
    } = useChartActionsMenu();

    useEffect(() => {
      if (!pinned && content) {
        setTooltipContent(content);
      }
    }, [content, pinned]);

    const seriesActions = useTooltipSeriesActions<TreeMapSeriesActionsLeafNode>(
      series,
      (nodeData) => nodeData.id,
    );

    const item: TooltipItemType = getTooltipItem(
      formatter,
      tooltipContent,
      unit,
    );

    const showTooltip = pinned || (mouseInBounds && visible);

    if (!showTooltip || !position || !tooltipContent) {
      return null;
    }

    const seriesActionsCallback = mergeTooltipActions(item, seriesActions);

    const nodeColor =
      tooltipContent.parent?.data.color?.Default ??
      tooltipContent.data?.color?.Default ??
      '';

    return (
      <ChartTooltip
        ref={forwardedRef}
        pinned={pinned}
        position={position}
        onMouseLeave={onLeave}
        actionSelectionValue={{
          selectedItemId,
          actionsMenuRef,
          updateSelectedItem,
        }}
      >
        {tooltipContent.parent?.data.name && (
          <TooltipHeader>
            <TooltipText truncationMode={truncationMode} className={headerText}>
              {tooltipContent.parent?.data.name}
            </TooltipText>
          </TooltipHeader>
        )}
        <TooltipBody virtualization={false}>
          <TooltipItem actions={seriesActionsCallback}>
            <TooltipSymbolContent>
              <SingleDataPointShape color={nodeColor} />
            </TooltipSymbolContent>
            <TooltipInfoContent>
              <TooltipText truncationMode={truncationMode}>
                {item.name as string}
              </TooltipText>
            </TooltipInfoContent>
            <TooltipValueContent variant="chip">
              <span className={valueChip}>{item.values?.[0]}</span>
            </TooltipValueContent>
          </TooltipItem>
        </TooltipBody>
      </ChartTooltip>
    );
  },
);
